import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Home, MapPin, Phone, MessageCircle, Star, ChevronRight, Save, Send,
  ArrowLeft, CheckCircle2, Clock, ClipboardList,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = (t) => ({ Authorization: `Bearer ${t}` });

const STATUS_META = {
  helper_assigned: { label: "Assigned", color: "#F97316", bg: "#FFEDD5" },
  on_site_visit: { label: "On-site visit", color: "#00604A", bg: "#DBEAFE" },
  report_pending: { label: "Report submitted · awaiting admin", color: "#7C3AED", bg: "#EDE9FE" },
  report_delivered: { label: "Delivered to customer", color: "#10B981", bg: "#D1FAE5" },
  payment_confirmed: { label: "Payment confirmed", color: "#F59E0B", bg: "#FEF3C7" },
  pending_payment: { label: "Pending payment", color: "#6B7280", bg: "#F3F4F6" },
  closed: { label: "Closed", color: "#6B7280", bg: "#F3F4F6" },
};

const REPORT_FIELDS = [
  { k: "overall_rating", label: "Overall rating", kind: "rating", hint: "Your gut-feel star rating (1–5)" },
  { k: "document_verification", label: "Document verification", hint: "Ownership / rent agreement / NOC / society permission — verified or not." },
  { k: "real_condition", label: "Real condition (what photos didn't show)", hint: "Walls, flooring, plumbing, electrical, water, drainage — good / average / needs attention." },
  { k: "issues_found", label: "Issues found", hint: "Leakage, pests, broken fixtures, noise, illegal construction, encroachment…" },
  { k: "owner_details", label: "Owner / landlord details", hint: "Owner present? Behaviour — cooperative / unavailable / evasive. Any red flags?" },
  { k: "agreement_terms", label: "Agreement & terms", hint: "Rent, deposit, lock-in, maintenance, any unusual clauses." },
  { k: "hidden_factors", label: "Surprises & hidden factors", hint: "Society/electricity/water/generator/parking extra. Restrictions not in the listing." },
  { k: "maid", label: "Maid & domestic help", hint: "Maid available in building/society? Approx monthly charges." },
  { k: "parking", label: "Parking", hint: "Available? Covered/open, 2W/4W, extra charges." },
  { k: "locality_notes", label: "Locality & surroundings", hint: "Market/metro/night safety/nuisances (bar, construction, traffic)." },
  { k: "extra_options", label: "Extra verified options nearby", hint: "2–3 nearby listings you saw + your quick take." },
  { k: "helper_recommendation", label: "Your final recommendation", hint: "Do you personally recommend this flat? Why or why not?" },
];

// -------------------- Task List --------------------
export function HelperTasks() {
  const { user, token, loading } = useAuth();
  const nav = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(true);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login?next=/helper"); return; }
    axios.get(`${API}/helper/flatmate/tasks`, { headers: auth(token) })
      .then((r) => setTasks(r.data.tasks || []))
      .catch(() => toast.error("Could not load your tasks"))
      .finally(() => setLoadingTasks(false));
  }, [user, token, loading, nav]);

  if (loading || !user) return null;

  const activeTasks = tasks.filter((t) => t.status !== "closed" && t.status !== "report_delivered");
  const completedTasks = tasks.filter((t) => t.status === "closed" || t.status === "report_delivered");

  return (
    <div className="max-w-2xl mx-auto px-4 md:px-6 py-6 pb-24" data-testid="helper-tasks">
      <div className="flex items-center gap-2">
        <div className="w-10 h-10 rounded-full grid place-items-center text-white flex-none"
             style={{ background: "linear-gradient(135deg, #10B981, #00604A)" }}>
          <ClipboardList className="w-5 h-5" />
        </div>
        <div>
          <div className="chip mb-0.5 text-[10px]" style={{ borderColor: "#10B981", color: "#10B981" }}>Helper task board</div>
          <h1 className="font-display text-2xl md:text-3xl leading-tight">On-site visits assigned to you</h1>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-2 gap-2">
        <StatChip label="Active" value={activeTasks.length} color="#F97316" />
        <StatChip label="Done" value={completedTasks.length} color="#10B981" />
      </div>

      {loadingTasks && <div className="text-neutral-500 mt-6">Loading…</div>}

      {!loadingTasks && tasks.length === 0 && (
        <div className="mt-8 rounded-2xl border border-dashed border-black/15 p-8 text-center text-sm text-neutral-500">
          No tasks yet. When admin assigns you a ₹99 Flatmate visit, it'll show up here.
        </div>
      )}

      {activeTasks.length > 0 && (
        <div className="mt-6">
          <div className="text-[11px] uppercase tracking-widest text-neutral-500 mb-2">To do</div>
          <div className="space-y-2" data-testid="active-tasks">
            {activeTasks.map((t) => <TaskCard key={t.id} t={t} />)}
          </div>
        </div>
      )}

      {completedTasks.length > 0 && (
        <div className="mt-8">
          <div className="text-[11px] uppercase tracking-widest text-neutral-500 mb-2">Done</div>
          <div className="space-y-2 opacity-70" data-testid="done-tasks">
            {completedTasks.map((t) => <TaskCard key={t.id} t={t} />)}
          </div>
        </div>
      )}
    </div>
  );
}

function StatChip({ label, value, color }) {
  return (
    <div className="rounded-2xl border border-black/10 bg-white p-3">
      <div className="text-[10px] uppercase tracking-widest text-neutral-500">{label}</div>
      <div className="font-display text-2xl mt-0.5" style={{ color }}>{value}</div>
    </div>
  );
}

function TaskCard({ t }) {
  const meta = STATUS_META[t.status] || { label: t.status, color: "#6B7280", bg: "#F3F4F6" };
  return (
    <Link to={`/helper/task/${t.id}`} data-testid={`task-${t.id}`}
          className="block rounded-2xl border border-black/10 bg-white p-4 card-lift">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-display text-base truncate">{t.area}, {t.city}</div>
          <div className="text-xs text-neutral-500 truncate">
            {t.customer_name} · Budget ₹{t.budget_inr?.toLocaleString?.("en-IN") || t.budget_inr}
          </div>
          {t.preference && (
            <div className="text-[11px] text-neutral-500 mt-1 line-clamp-2">Note: {t.preference}</div>
          )}
        </div>
        <div className="flex flex-col items-end gap-2 flex-none">
          <span className="text-[10px] font-bold rounded-full px-2 py-0.5 whitespace-nowrap"
                style={{ background: meta.bg, color: meta.color }}>
            {meta.label}
          </span>
          <ChevronRight className="w-4 h-4 text-neutral-400" />
        </div>
      </div>
      <div className="text-[10px] text-neutral-400 mt-2">{new Date(t.created_at).toLocaleString()}</div>
    </Link>
  );
}

// -------------------- Task Detail (12-field report) --------------------
export function HelperTaskDetail() {
  const { jid } = useParams();
  const { user, token, loading } = useAuth();
  const nav = useNavigate();
  const [task, setTask] = useState(null);
  const [report, setReport] = useState({});
  const [busy, setBusy] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav(`/login?next=/helper/task/${jid}`); return; }
    axios.get(`${API}/helper/flatmate/tasks/${jid}`, { headers: auth(token) })
      .then((r) => { setTask(r.data.task); setReport(r.data.task.report || {}); })
      .catch((e) => {
        if (e?.response?.status === 403) toast.error("This task isn't assigned to you.");
        else toast.error("Could not load task");
        nav("/helper");
      });
  }, [jid, user, token, loading, nav]);

  const save = async (submit = false) => {
    if (submit) setSubmitting(true); else setBusy(true);
    try {
      const r = await axios.post(`${API}/helper/flatmate/tasks/${jid}/report`,
        { report, submit }, { headers: auth(token) });
      toast.success(submit ? "Report submitted for admin review 🎉" : "Draft saved");
      setTask((t) => ({ ...t, status: r.data.status }));
      if (submit) setTimeout(() => nav("/helper"), 800);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); setSubmitting(false); }
  };

  if (!task) return <div className="max-w-2xl mx-auto p-8 text-neutral-500">Loading…</div>;

  const meta = STATUS_META[task.status] || { label: task.status, color: "#6B7280", bg: "#F3F4F6" };
  const canEdit = task.status !== "report_delivered" && task.status !== "closed";
  const waMsg = encodeURIComponent(
    `Hi ${task.customer_name}, I'm your HeylpMATE helper for the ${task.area} property visit. When is a good time to connect?`
  );
  const waUrl = task.customer_mobile ? `https://wa.me/91${String(task.customer_mobile).replace(/\D/g, "").slice(-10)}?text=${waMsg}` : null;

  return (
    <div className="max-w-2xl mx-auto px-4 md:px-6 py-6 pb-32" data-testid="helper-task-detail">
      <Link to="/helper" className="inline-flex items-center gap-1 text-sm text-neutral-500 hover:text-neutral-800">
        <ArrowLeft className="w-4 h-4" /> Back to tasks
      </Link>

      <div className="mt-3 rounded-3xl border border-black/10 bg-white p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="chip text-[10px] mb-1" style={{ background: meta.bg, color: meta.color, borderColor: meta.color + "33" }}>
              {meta.label}
            </div>
            <div className="font-display text-xl">{task.area}, {task.city}</div>
            <div className="text-sm text-neutral-500 flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> Budget ₹{task.budget_inr?.toLocaleString?.("en-IN")}</div>
          </div>
          <Home className="w-8 h-8 text-neutral-300" />
        </div>

        <div className="mt-4 grid grid-cols-2 gap-2">
          {task.customer_mobile ? (
            <a href={`tel:${task.customer_mobile}`} data-testid="task-call"
               className="rounded-2xl px-3 py-2.5 text-white text-sm font-bold inline-flex items-center justify-center gap-2"
               style={{ background: "#00604A" }}>
              <Phone className="w-4 h-4" /> Call {task.customer_name?.split(" ")[0]}
            </a>
          ) : (
            <div className="rounded-2xl px-3 py-2.5 bg-neutral-100 text-neutral-500 text-xs text-center">Customer mobile not on file</div>
          )}
          {waUrl ? (
            <a href={waUrl} target="_blank" rel="noreferrer" data-testid="task-whatsapp"
               className="rounded-2xl px-3 py-2.5 text-white text-sm font-bold inline-flex items-center justify-center gap-2"
               style={{ background: "#10B981" }}>
              <MessageCircle className="w-4 h-4" /> WhatsApp
            </a>
          ) : null}
        </div>

        {task.preference && (
          <div className="mt-3 rounded-xl bg-neutral-50 p-3 text-xs">
            <div className="font-bold text-neutral-500 uppercase tracking-widest text-[10px]">Customer's note</div>
            <div className="mt-1 text-neutral-700">{task.preference}</div>
          </div>
        )}
        {task.shortlist_url && (
          <a href={task.shortlist_url} target="_blank" rel="noreferrer"
             className="mt-2 inline-flex items-center gap-1 text-xs text-emerald-700 underline">
            Open shortlist link →
          </a>
        )}
      </div>

      {/* REPORT FORM */}
      <div className="mt-6">
        <h2 className="font-display text-xl">Verified Property Report</h2>
        <p className="text-xs text-neutral-500 mt-1">Fill on-site. Draft auto-saves when you tap Save. Submit sends it to admin for review.</p>

        <div className="mt-4 space-y-4">
          {REPORT_FIELDS.map((f) => (
            <ReportField
              key={f.k}
              field={f}
              value={report[f.k] || ""}
              onChange={(v) => setReport((r) => ({ ...r, [f.k]: v }))}
              disabled={!canEdit}
            />
          ))}
        </div>
      </div>

      {canEdit ? (
        <div className="fixed bottom-0 left-0 right-0 border-t border-black/10 bg-white/95 backdrop-blur p-3 z-40">
          <div className="max-w-2xl mx-auto flex gap-2">
            <button onClick={() => save(false)} disabled={busy || submitting}
                    data-testid="save-draft"
                    className="flex-1 rounded-2xl border border-black/10 bg-white px-4 py-3 text-sm font-bold inline-flex items-center justify-center gap-1 disabled:opacity-50">
              <Save className="w-4 h-4" /> {busy ? "Saving…" : "Save draft"}
            </button>
            <button onClick={() => save(true)} disabled={busy || submitting}
                    data-testid="submit-report"
                    className="flex-1 rounded-2xl px-4 py-3 text-white text-sm font-bold inline-flex items-center justify-center gap-1 disabled:opacity-50 shadow-lg"
                    style={{ background: "linear-gradient(135deg, #10B981, #00604A)" }}>
              <Send className="w-4 h-4" /> {submitting ? "Submitting…" : "Submit to admin"}
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-6 rounded-2xl bg-emerald-50 border border-emerald-200 p-4 text-sm text-emerald-800 inline-flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4" /> Report already delivered — read-only.
        </div>
      )}
    </div>
  );
}

function ReportField({ field, value, onChange, disabled }) {
  if (field.kind === "rating") {
    const n = parseInt(value || "0") || 0;
    return (
      <div className="rounded-2xl border border-black/10 bg-white p-4">
        <div className="font-bold text-sm">{field.label}</div>
        <div className="text-[11px] text-neutral-500 mt-0.5">{field.hint}</div>
        <div className="mt-2 flex gap-1" data-testid={`field-${field.k}`}>
          {[1, 2, 3, 4, 5].map((i) => (
            <button key={i} type="button" onClick={() => !disabled && onChange(String(i))}
                    data-testid={`rating-${i}`}
                    disabled={disabled}
                    className={`w-10 h-10 rounded-full grid place-items-center transition ${i <= n ? "bg-amber-400 text-white" : "bg-neutral-100 text-neutral-400"}`}>
              <Star className={`w-5 h-5 ${i <= n ? "fill-current" : ""}`} />
            </button>
          ))}
        </div>
      </div>
    );
  }
  return (
    <div className="rounded-2xl border border-black/10 bg-white p-4">
      <label className="block">
        <div className="font-bold text-sm">{field.label}</div>
        <div className="text-[11px] text-neutral-500 mt-0.5">{field.hint}</div>
        <textarea
          data-testid={`field-${field.k}`}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          rows={3}
          placeholder="Type here…"
          className="mt-2 w-full rounded-xl border border-black/10 bg-white p-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 disabled:bg-neutral-50 disabled:text-neutral-500"
        />
      </label>
    </div>
  );
}
